import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Search, CheckCircle2, XCircle, User } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input } from '@/components/ui/Field';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

interface SoldOrderInfo {
  id: string;
  order_no: string;
  placed_at: string;
  channel: string;
  status: string;
  customer_info: { name?: string; phone?: string; address?: string } | null;
  core_partners: { name: string } | null;
}

interface CodeSearchResult {
  code: string;
  inventory_code: string;
  sku: string;
  lot_no: string | null;
  status: string;
  verify_count: number;
  first_verified_at: string | null;
  last_verified_at: string | null;
  sold_order_id: string | null;
  core_products: { name_en: string | null } | null;
  core_inventory_lots: { mfg_date: string | null; expiry_date: string | null; warehouse: string } | null;
  core_orders: SoldOrderInfo | null;
}

export default function AdminVerifyCode() {
  const { push } = useToast();
  const navigate = useNavigate();
  const [searchCode, setSearchCode] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<CodeSearchResult | null | 'not_found'>(null);

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    if (!searchCode.trim()) return;
    setSearching(true);
    setSearchResult(null);
    const term = searchCode.trim().toUpperCase();
    const { data, error } = await supabase
      .from('core_product_authenticity_codes')
      .select(
        'code, inventory_code, sku, lot_no, status, verify_count, first_verified_at, last_verified_at, sold_order_id, core_products(name_en), core_inventory_lots(mfg_date, expiry_date, warehouse), core_orders:sold_order_id(id, order_no, placed_at, channel, status, customer_info, core_partners(name))',
      )
      .or(`code.eq.${term},inventory_code.eq.${term}`)
      .maybeSingle();
    setSearching(false);
    if (error) {
      push('error', error.message);
      return;
    }
    setSearchResult((data as unknown as CodeSearchResult) ?? 'not_found');
  };

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="max-w-5xl space-y-5">
        <div>
          <h1 className="text-[19px] font-bold text-ink">Check a Code</h1>
          <p className="text-[12.5px] text-ink-faint">
            Look up a customer's product code — the QR verify code or the barcode's inventory code both work. For generating new
            codes or printing labels, use ERP.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <Card>
          <CardHeader title="Check a Code" subtitle="Look up whether a code exists in our system" icon={<ShieldCheck className="w-4 h-4" />} />
          <CardBody className="space-y-3.5">
            <form onSubmit={handleSearch} className="flex items-end gap-2.5">
              <div className="flex-1">
                <Field label="Code">
                  <Input
                    value={searchCode}
                    onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                    placeholder="e.g. RXKBST50-EFWD-7H5X"
                    className="font-mono"
                  />
                </Field>
              </div>
              <Button type="submit" loading={searching} disabled={!searchCode.trim()}>
                <Search className="w-4 h-4" /> Check
              </Button>
            </form>

            {searchResult === 'not_found' && (
              <EmptyState icon={<XCircle className="w-5 h-5 text-error" />} title="Code not found" />
            )}

            {searchResult && searchResult !== 'not_found' && (
              <div className="rounded-xl border border-border-subtle divide-y divide-border-subtle">
                <div className="flex items-center gap-2 px-4 py-3 bg-success-bg">
                  <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                  <p className="text-[12.5px] font-semibold text-success">Code exists in our system</p>
                </div>
                {[
                  ['Product', searchResult.core_products?.name_en || searchResult.sku],
                  ['SKU', searchResult.sku],
                  ['Verify Code (QR)', searchResult.code],
                  ['Inventory Code (Barcode)', searchResult.inventory_code],
                  ['Lot Number', searchResult.lot_no],
                  ['Warehouse', searchResult.core_inventory_lots?.warehouse],
                  ['Mfg Date', searchResult.core_inventory_lots?.mfg_date],
                  ['Expiry Date', searchResult.core_inventory_lots?.expiry_date],
                  ['First Scanned', searchResult.first_verified_at ? new Date(searchResult.first_verified_at).toLocaleString() : null],
                  ['Last Scanned', searchResult.last_verified_at ? new Date(searchResult.last_verified_at).toLocaleString() : null],
                  ['Scan Count', String(searchResult.verify_count)],
                ]
                  .filter(([, value]) => !!value)
                  .map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-[12px] text-ink-faint">{label}</span>
                      <span className="text-[12.5px] font-semibold text-ink font-mono">{value}</span>
                    </div>
                  ))}
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-[12px] text-ink-faint">Status</span>
                  <Badge tone={searchResult.status === 'unused' ? 'warning' : 'success'}>{searchResult.status}</Badge>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {searchResult && searchResult !== 'not_found' && searchResult.core_orders && (
          <Card>
            <CardHeader title="Sold To" subtitle="Who this exact unit was sold to" icon={<User className="w-4 h-4" />} />
            <CardBody className="space-y-0 divide-y divide-border-subtle">
              {[
                ['Order No', searchResult.core_orders.order_no],
                ['Customer Name', searchResult.core_orders.customer_info?.name],
                ['Phone', searchResult.core_orders.customer_info?.phone],
                ['Address', searchResult.core_orders.customer_info?.address],
                ['Channel', searchResult.core_orders.channel],
                ['Order Status', searchResult.core_orders.status],
                ['Sold Via Partner', searchResult.core_orders.core_partners?.name],
                ['Placed At', new Date(searchResult.core_orders.placed_at).toLocaleString()],
              ]
                .filter(([, value]) => !!value)
                .map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-[12px] text-ink-faint">{label}</span>
                    <span className="text-[12.5px] font-semibold text-ink text-right">{value}</span>
                  </div>
                ))}
              {searchResult.core_orders.core_partners && (
                <p className="px-4 py-2.5 text-[11.5px] text-ink-faint bg-warning-bg/40">
                  This order went to a reseller/partner. If they resold this specific unit to their own customer, that end
                  buyer isn't tracked here — partner resales are logged by quantity only, not by serial.
                </p>
              )}
              <div className="px-4 py-3">
                <Button size="sm" variant="secondary" onClick={() => navigate(`/admin/orders/${searchResult.core_orders!.id}`)}>
                  View Full Order
                </Button>
              </div>
            </CardBody>
          </Card>
        )}
        </div>
      </div>
    </AppShell>
  );
}
